/** Cookie-sourced embed params (`param_field` with `source: 'cookie'`). */

import type { WidgetParamField } from './types';

/** Read the present, non-empty cookies among `names`, URL-decoded. */
export function readCookies(names: string[]): Record<string, string> {
  if (!names.length || typeof document === 'undefined') return {};
  const jar: Record<string, string> = {};
  for (const pair of document.cookie.split(';')) {
    const eq = pair.indexOf('=');
    if (eq === -1) continue;
    const key = pair.slice(0, eq).trim();
    if (key) jar[key] = pair.slice(eq + 1).trim();
  }
  const result: Record<string, string> = {};
  for (const name of names) {
    const raw = jar[name];
    if (raw === undefined || raw === '') continue;
    try {
      result[name] = decodeURIComponent(raw);
    } catch {
      result[name] = raw;
    }
  }
  return result;
}

/** Cookie names named by the cookie-source param fields. */
function cookieFieldNames(paramFields: WidgetParamField[]): string[] {
  return paramFields
    .filter((f) => f.source === 'cookie' && !!f.cookieName)
    .map((f) => f.cookieName as string);
}

/** Params contributed by cookie-source fields, keyed by each field's declared `key`. */
export function readCookieParams(paramFields: WidgetParamField[]): Record<string, string> {
  const jar = readCookies(cookieFieldNames(paramFields));
  const out: Record<string, string> = {};
  for (const f of paramFields) {
    if (f.source === 'cookie' && f.cookieName && jar[f.cookieName] !== undefined) {
      out[f.key] = jar[f.cookieName];
    }
  }
  return out;
}

/**
 * The `context.cookies` emitted on form submit, keyed by cookie name. Read
 * fresh so a cookie set after init (a mid-chat login) is captured.
 */
export function readCookieShim(paramFields: WidgetParamField[]): Record<string, string> {
  return readCookies(cookieFieldNames(paramFields));
}
